import { tokens } from '@sparkpost/design-tokens';
import { Send } from '@sparkpost/matchbox-icons';
import React, { useCallback } from 'react';
import { ExternalLink, SubduedLink } from 'src/components/links';
import {
  Box,
  Panel,
  Stack,
  Tag,
  Text,
  Toggle,
  Layout,
  Inline,
  Button
} from 'src/components/matchbox';
import { Bold, Heading, SubduedText, TranslatableText } from 'src/components/text';
import { useModal } from 'src/hooks';
import DeleteAlertModal from './DeleteAlertModal';

export default function AlertOverView({ alert }) {
  const { openModal, closeModal, isModalOpen } = useModal();

  const handleSendAlertTest = useCallback(() => {
    // TODO: Implement test send alert
  }, []);

  return (
    <Box>
      <Layout>
        <Layout.Section annotated>
          <Stack>
            <Heading as="h4">Notification Settings</Heading>
            <SubduedText>An alert requires at least 1 notification channel.</SubduedText>
            <Text>
              To learn how to create a unique URL for Slack integration, reference Slack’s
            </Text>
            <SubduedLink
              as={ExternalLink}
              to="https://www.sparkpost.com/blog/creating-and-consuming-sparkpost-webhooks/"
            >
              Webhook Article
            </SubduedLink>
            <SubduedLink as={ExternalLink} to="https://developers.sparkpost.com/api/webhooks/">
              Webhook Payload Sample
            </SubduedLink>
          </Stack>
        </Layout.Section>
        <Layout.Section>
          <Panel>
            <Panel.Section>
              <Stack space="100">
                <Box display="flex" justifyContent="space-between">
                  <Inline space="800">
                    <Bold>Enable Notification</Bold>
                    <Toggle
                      paddingTop="200"
                      data-id="alert-notification-toggle-input"
                      label="Enable Notification"
                      id="alert-notification-toggle"
                    />
                  </Inline>
                  <Panel.Action onClick={handleSendAlertTest}>
                    Send a Test <Button.Icon as={Send} />
                  </Panel.Action>
                </Box>

                <TranslatableText>
                  Alert notifications will be sent to the specified channels.
                </TranslatableText>
              </Stack>
            </Panel.Section>
            <Panel.SubHeader>Notification Channels</Panel.SubHeader>
            <Panel.Section>
              <Stack space="400">
                <Box>
                  <Bold>Email</Bold>
                  {alert.channels.emails.map((email) => (
                    <Box key={`alert-overview-email-${email}`} mb={tokens.spacing_200}>
                      <Tag>{email}</Tag>
                    </Box>
                  ))}
                </Box>
                <Box>
                  <Bold>Slack</Bold>
                  {alert.channels.slack.target ? (
                    <Box mb={tokens.spacing_200}>
                      <Tag>{alert.channels.slack.target}</Tag>
                    </Box>
                  ) : (
                    <Text>None</Text>
                  )}
                </Box>
                <Box>
                  <Bold>Webhook</Bold>
                  {alert.channels.webhook.target ? (
                    <Box mb={tokens.spacing_200}>
                      <Tag>{alert.channels.webhook.target}</Tag>
                    </Box>
                  ) : (
                    <Text>None</Text>
                  )}
                </Box>
              </Stack>
            </Panel.Section>
            <Panel.SubHeader>Notification Time Range</Panel.SubHeader>
            <Box pl={tokens.spacing_450} pr={tokens.spacing_450} pt={tokens.spacing_300}>
              The alert is triggered if the conditions are met during the configured time range.
            </Box>
            <Panel.Section>
              <Stack space="500">
                <Box>
                  <Box>
                    <Bold>Days</Bold>
                  </Box>
                  {alert.days.map((day) => (
                    <Tag key={`alert-overview-day-${day}`} mr={tokens.spacing_200}>
                      <TranslatableText>{day}</TranslatableText>
                    </Tag>
                  ))}
                </Box>
                <Box>
                  <Bold>Timeframe</Bold>
                  <Box mb={tokens.spacing_200}>
                    <Text>
                      {alert.timeframe.from} to {alert.timeframe.to}, {alert.timeframe.timezone}
                    </Text>
                  </Box>
                </Box>
              </Stack>
            </Panel.Section>
          </Panel>
        </Layout.Section>
      </Layout>
      <Layout>
        <Layout.Section annotated>
          <Stack space="300">
            <Heading as="h4">Delete Alert</Heading>
          </Stack>
        </Layout.Section>
        <Layout.Section>
          <Panel borderTop="4px solid #D9363E">
            <Box padding={tokens.spacing_450}>
              <Text color={tokens.color_gray_900}>
                Deleting an alert is permanent and cannot be undone.
              </Text>
            </Box>
          </Panel>
          <Panel>
            <Panel.Section>
              <Button color="red" variant="outline-destructive" onClick={openModal}>
                Delete Alert
              </Button>
            </Panel.Section>
          </Panel>
        </Layout.Section>

        <DeleteAlertModal open={isModalOpen} onClose={closeModal} alert={alert} />
      </Layout>
    </Box>
  );
}
